import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Globe, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryPage = () => {
  const { slug } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    fetchCountry();
  }, [slug]);

  const fetchCountry = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/countries/${slug}`);
      setCountry(res.data);
    } catch (err) {
      console.error('Error fetching country:', err);
      setError('Country not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="country-page" data-testid="country-page">
        <div className="country-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !country) {
    return (
      <div className="country-page" data-testid="country-page">
        <div className="country-error">
          <Globe size={48} />
          <h2>Country Not Found</h2>
          <p>The country you're looking for doesn't exist or has been moved.</p>
          <Link to="/jnt-tales" className="back-to-tales-btn">
            <ArrowLeft size={18} />
            Back to JNT Tales
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="country-page" data-testid="country-page">
      {/* Hero Section */}
      <section className="country-hero">
        {country.hero_image && (
          <img 
            src={country.hero_image} 
            alt={country.image_alt || country.name} 
            className="country-hero-image"
          />
        )}
        <div className="country-hero-overlay"></div>
        <div className="country-hero-content">
          <div className="country-continent-badge">{country.continent}</div>
          <h1 className="country-hero-title">{country.name}</h1>
          <Link to="/jnt-tales" className="country-back-link" data-testid="back-to-tales">
            <ArrowLeft size={18} />
            <span>Back to JNT Tales</span>
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <div className="country-content-wrapper">
        {/* Main Column */}
        <div className="country-main">
          {/* Description */}
          <div className="country-description">
            <p>{country.description}</p>
          </div>

          {/* Highlights */}
          {country.highlights && (
            <div className="country-highlights">
              <h3>Highlights</h3>
              <p>{country.highlights}</p>
            </div>
          )}

          {/* Cities Section */}
          <section className="country-cities-section">
            <h2 className="cities-section-title">
              <MapPin size={24} />
              Destinations in {country.name}
            </h2>
            
            {country.cities && country.cities.length > 0 ? (
              <div className="cities-grid">
                {country.cities.map((city) => (
                  <Link 
                    to={`/jnt-tales/${city.slug}`} 
                    key={city.id} 
                    className="city-card"
                    data-testid={`city-card-${city.slug}`}
                  >
                    <div className="city-card-image">
                      {city.featured_image ? (
                        <img src={city.featured_image} alt={city.title} />
                      ) : (
                        <div className="city-card-placeholder">
                          <MapPin size={24} />
                        </div>
                      )}
                    </div>
                    <div className="city-card-content">
                      <h3>{city.title}</h3>
                      <p>{city.introduction?.substring(0, 100)}...</p>
                      {city.best_time && (
                        <span className="city-best-time">
                          <Calendar size={14} />
                          {city.best_time}
                        </span>
                      )}
                      <span className="city-read-more">
                        Explore <ChevronRight size={16} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="cities-empty">
                <MapPin size={48} />
                <p>No destinations added yet for {country.name}</p>
              </div>
            )}
          </section>
        </div>

        {/* Sidebar - Other Cities Navigation */}
        {country.cities && country.cities.length > 1 && (
          <aside className="country-sidebar">
            <div className="sidebar-section">
              <h3>Quick Navigation</h3>
              <ul className="sidebar-cities-list">
                {country.cities.map((city) => (
                  <li key={city.id}>
                    <Link to={`/jnt-tales/${city.slug}`}>
                      {city.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>

      {/* CTA Section */}
      <section className="country-cta-section">
        <div className="country-cta-content">
          <h2>Ready to Explore {country.name}?</h2>
          <p>Let us design your perfect journey</p>
          <Link to="/design-journey" className="country-cta-btn" data-testid="country-cta-btn">
            Design My Journey
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CountryPage;
